/**
 * KeyboardShortcutsHelp.jsx
 * Modal de ajuda com lista de atalhos de teclado
 *
 * Features:
 * - Lista agrupada por categoria
 * - Visual premium com tokens
 * - Suporte a i18n
 * - Aberto com "?"
 *
 * Uso:
 *   <KeyboardShortcutsHelp />
 *   // Automaticamente conectado ao KeyboardShortcutsProvider
 */

import React, { useMemo, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Fade,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';
import { useKeyboardShortcutsContext } from '../KeyboardShortcutsProvider';
import { formatShortcut } from '../../hooks/useKeyboardShortcuts';

// ============================================================================
// DEFINIÇÃO DOS ATALHOS PARA EXIBIÇÃO
// ============================================================================

const getShortcutGroups = (t) => [
  {
    id: 'quick',
    title: t('shortcutQuickActions') || 'Quick Actions',
    shortcuts: [
      { keys: 'ctrl+k', description: t('shortcutCommandBar') },
      { keys: '?', description: t('shortcutHelp') },
      { keys: 'escape', description: t('shortcutClose') },
    ],
  },
  {
    id: 'navigation',
    title: t('shortcutNavigation'),
    shortcuts: [
      { keys: 'g m', description: t('shortcutGoMap') },
      { keys: 'g v', description: t('shortcutGoVehicles') },
      { keys: 'g d', description: t('shortcutGoDrivers') },
      { keys: 'g g', description: t('shortcutGoGeofences') },
      { keys: 'g r', description: t('shortcutGoReports') },
      { keys: 'g s', description: t('shortcutGoSettings') },
      { keys: 'g u', description: t('shortcutGoUsers') },
      { keys: 'g n', description: t('shortcutGoNotifications') },
      { keys: 'g f', description: t('shortcutGoFleet') },
    ],
  },
];

// ============================================================================
// COMPONENTE DE TECLA
// ============================================================================

const KeyBadge = ({ children, tokens }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      height: 24,
      px: 1,
      backgroundColor: tokens.surface?.sunken || '#1c2128',
      border: `1px solid ${tokens.border?.default || 'rgba(255,255,255,0.1)'}`,
      borderRadius: tokens.radius?.sm || '4px',
      fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
      fontFamily: tokens.typography?.fontFamily?.mono || 'monospace',
      fontWeight: tokens.typography?.fontWeight?.medium || 500,
      color: tokens.text?.primary || '#f0f6fc',
      boxShadow: `0 1px 0 ${tokens.border?.default || 'rgba(255,255,255,0.1)'}`,
    }}
  >
    {children}
  </Box>
);

// ============================================================================
// COMPONENTE DE LINHA DE ATALHO
// ============================================================================

const ShortcutRow = ({ keys, description, tokens, t }) => {
  // Formatar teclas para exibição
  const formattedKeys = useMemo(() => formatShortcut(keys), [keys]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
        px: 1,
        borderRadius: tokens.radius?.md || '6px',
        transition: tokens.transitions?.colors || 'color 0.15s ease',
        '&:hover': {
          backgroundColor: tokens.surface?.hover || 'rgba(255,255,255,0.05)',
        },
      }}
    >
      {/* Descrição */}
      <Typography
        sx={{
          fontSize: tokens.typography?.fontSize?.sm || '0.875rem',
          color: tokens.text?.secondary || '#8b949e',
        }}
      >
        {description}
      </Typography>

      {/* Teclas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {formattedKeys.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && (
              <Typography
                sx={{
                  mx: 0.5,
                  fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
                  color: tokens.text?.muted || '#6e7681',
                }}
              >
                {t('shortcutThen') || 'then'}
              </Typography>
            )}
            {group.map((key, keyIndex) => (
              <React.Fragment key={keyIndex}>
                {keyIndex > 0 && (
                  <Typography
                    sx={{
                      mx: 0.25,
                      fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
                      color: tokens.text?.muted || '#6e7681',
                    }}
                  >
                    +
                  </Typography>
                )}
                <KeyBadge tokens={tokens}>{key}</KeyBadge>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const KeyboardShortcutsHelp = () => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  // Pegar estado do contexto (se disponível)
  let helpOpen = false;
  let setHelpOpen = () => {};

  try {
    const context = useKeyboardShortcutsContext();
    helpOpen = context.helpOpen;
    setHelpOpen = context.setHelpOpen;
  } catch {
    // Se não estiver dentro do provider, não renderiza
    return null;
  }

  const shortcutGroups = useMemo(() => getShortcutGroups(t), [t]);

  // Blur active element when modal opens to prevent aria-hidden conflict
  useEffect(() => {
    if (helpOpen && document.activeElement) {
      document.activeElement.blur();
    }
  }, [helpOpen]);

  const handleClose = () => setHelpOpen(false);

  return (
    <Modal
      open={helpOpen}
      onClose={handleClose}
      closeAfterTransition
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <Fade in={helpOpen}>
        <Box
          tabIndex={-1}
          sx={{
            width: '100%',
            maxWidth: 480,
            maxHeight: '80vh',
            backgroundColor: tokens.surface?.elevated || '#1c2128',
            borderRadius: tokens.radius?.xl || '12px',
            border: `1px solid ${tokens.border?.subtle || 'rgba(255,255,255,0.1)'}`,
            boxShadow: tokens.shadows?.xl || '0 25px 50px -12px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: `1px solid ${tokens.border?.subtle || 'rgba(255,255,255,0.1)'}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: tokens.typography?.fontWeight?.semibold || 600,
                color: tokens.text?.primary || '#f0f6fc',
              }}
            >
              {t('keyboardShortcuts')}
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: tokens.text?.tertiary || '#6e7681',
                '&:hover': {
                  backgroundColor: tokens.surface?.hover || 'rgba(255,255,255,0.05)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              maxHeight: 'calc(80vh - 60px)',
              overflow: 'auto',
              p: 2,
            }}
          >
            {shortcutGroups.map((group, index) => (
              <Box key={group.id} sx={{ mb: index < shortcutGroups.length - 1 ? 3 : 0 }}>
                {/* Group Title */}
                <Typography
                  sx={{
                    px: 1,
                    mb: 1,
                    fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
                    fontWeight: tokens.typography?.fontWeight?.semibold || 600,
                    color: tokens.text?.muted || '#6e7681',
                    textTransform: 'uppercase',
                    letterSpacing: tokens.typography?.letterSpacing?.wider || '0.05em',
                  }}
                >
                  {group.title}
                </Typography>

                {/* Shortcuts */}
                <Box
                  sx={{
                    backgroundColor: tokens.surface?.card || '#161b22',
                    borderRadius: tokens.radius?.lg || '8px',
                    border: `1px solid ${tokens.border?.subtle || 'rgba(255,255,255,0.1)'}`,
                    overflow: 'hidden',
                  }}
                >
                  {group.shortcuts.map((shortcut, idx) => (
                    <React.Fragment key={shortcut.keys}>
                      {idx > 0 && (
                        <Divider sx={{ borderColor: tokens.border?.subtle || 'rgba(255,255,255,0.1)' }} />
                      )}
                      <ShortcutRow
                        keys={shortcut.keys}
                        description={shortcut.description}
                        tokens={tokens}
                        t={t}
                      />
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderTop: `1px solid ${tokens.border?.subtle || 'rgba(255,255,255,0.1)'}`,
              backgroundColor: tokens.surface?.sunken || '#0d1117',
            }}
          >
            <Typography
              sx={{
                fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
                color: tokens.text?.muted || '#6e7681',
                textAlign: 'center',
              }}
            >
              {t('shortcutHelp')}: <KeyBadge tokens={tokens}>?</KeyBadge>
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

KeyboardShortcutsHelp.displayName = 'KeyboardShortcutsHelp';

export default KeyboardShortcutsHelp;
